'use client';

import { useState, useEffect } from 'react';
import { useClientTranslation } from '@/lib/core';
import { Drawer, DrawerContent, DrawerFooter } from '@/components/ui/drawer';

const MAX_LENGTH = 2000;

// ─── PreferencePersonalizeSheet ───────────────────────────────────────────────

export interface PreferencePersonalizeSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-fills the textarea with the user's existing value. */
  initialValue?: string | null;
  title?: string;
  label?: string;
  placeholder?: string;
  saveLabel?: string;
  cancelLabel?: string;
  onSave: (value: string | null) => Promise<void>;
}

export function PreferencePersonalizeSheet({
  open,
  onOpenChange,
  initialValue,
  title,
  label,
  placeholder,
  saveLabel,
  cancelLabel,
  onSave,
}: PreferencePersonalizeSheetProps) {
  const t = useClientTranslation();
  const _title = title ?? t('App.Labels.PersonalizeSheetTitle');
  const _label = label ?? t('App.Labels.PersonalizeSheetLabel');
  const _placeholder = placeholder ?? t('App.Labels.PersonalizeSheetPlaceholder');
  const _saveLabel = saveLabel ?? t('App.Labels.SaveChanges');
  const _cancelLabel = cancelLabel ?? t('App.Labels.Cancel');

  const [value, setValue] = useState(initialValue ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setValue(initialValue ?? '');
  }, [open, initialValue]);

  async function handleSave() {
    setSaving(true);
    try {
      await onSave(value.trim() || null);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="pt-4">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3.75">
          <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 w-full text-center text-[14px] tracking-[0.5px] uppercase">
            {_title}
          </p>
        </div>

        {/* Body */}
        <div className="flex flex-col gap-1 px-4 pt-6 pb-10">
          <p className="text-jncquoi-neutral-1000 font-inter text-[14px] font-medium tracking-[0.5px]">
            {_label}
          </p>
          <textarea
            value={value}
            maxLength={MAX_LENGTH}
            placeholder={_placeholder}
            onChange={e => setValue(e.target.value)}
            className="border-jncquoi-neutral-200 font-pp-neue-montreal text-jncquoi-neutral-1000 placeholder:text-jncquoi-neutral-600 h-20 w-full resize-none border px-3.75 py-2 text-[14px] tracking-[0.5px] outline-none"
          />
          <p className="font-pp-neue-montreal text-jncquoi-neutral-800 text-[12px] tracking-[0.5px]">
            {value.length}/{MAX_LENGTH}
          </p>
        </div>

        {/* Footer */}
        <DrawerFooter className="gap-1">
          <button
            type="button"
            disabled={saving}
            onClick={handleSave}
            className="bg-jncquoi-neutral-1000 flex h-11.5 w-full items-center justify-center rounded-sm disabled:opacity-60"
          >
            <span className="font-pp-neue-montreal text-[12px] font-semibold tracking-[0.5px] text-white uppercase">
              {_saveLabel}
            </span>
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={() => onOpenChange(false)}
            className="flex h-9 w-full items-center justify-center rounded-sm"
          >
            <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] tracking-[0.5px] uppercase">
              {_cancelLabel}
            </span>
          </button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
